/** Products, variants, prices and catalog reference data (docs/specs/catalog.md). */
export const catalog = {
    products: {
        title: "Products",
        description: "Everything you sell, with its variants, prices and barcodes.",
        add: "Add product",
        back: "All products",
        empty: "No products yet. Add your first product.",
        emptyArchived: "No archived products.",
        columns: { name: "Name", category: "Category", variants: "Variants", price: "Price", status: "Status" },
        filters: { status: "Status", category: "Category", all: "All" },
        created: "Product added.",
        saved: "Product saved.",
        archive: "Archive product",
        unarchive: "Bring back",
        archiveTitle: "Archive this product?",
        archiveBody: "It disappears from the POS and search. Its history stays in reports.",
        archived: "Product archived.",
        unarchived: "Product is active again.",
    },
    statuses: { active: "Active", archived: "Archived", trashed: "In the Trash", deleted: "Deleted" },
    bin: {
        trash: "Trash",
        emptyTrash: "No products in the Trash.",
        selected_one: "{{count}} product selected",
        selected_other: "{{count}} products selected",
        delete: "Delete",
        archive: "Archive",
        unarchive: "Bring back",
        restore: "Restore",
        deleteForever: "Delete permanently",
        empty: "Empty Trash",
        deleteTitle_one: "Delete this product?",
        deleteTitle_other: "Delete {{count}} products?",
        deleteBody: "Deleted products go to the Trash, where you can restore them. Products with sales or stock history are archived instead, so your reports stay right.",
        permanent: "Delete permanently now (skip the Trash)",
        permanentHint: "This cannot be undone. Their SKUs and barcodes can be used again.",
        foreverTitle_one: "Delete this product permanently?",
        foreverTitle_other: "Delete {{count}} products permanently?",
        foreverBody: "This cannot be undone. Products that gained history are archived instead.",
        emptyTitle: "Empty the Trash?",
        emptyBody: "Every product in the Trash is deleted permanently. This cannot be undone.",
        trashed_one: "{{count}} product moved to the Trash.",
        trashed_other: "{{count}} products moved to the Trash.",
        deleted_one: "{{count}} product deleted permanently.",
        deleted_other: "{{count}} products deleted permanently.",
        archived_one: "{{count}} product archived instead: it has sales or stock history.",
        archived_other: "{{count}} products archived instead: they have sales or stock history.",
        restored_one: "{{count}} product restored.",
        restored_other: "{{count}} products restored.",
        changed_one: "{{count}} product updated.",
        changed_other: "{{count}} products updated.",
        remaining: "{{count}} more are still in the Trash: empty it again.",
        trashedOn: "Deleted on",
    },
    details: "Product details",
    types: { standard: "Standard", weighed: "Sold by weight", service: "Service (no stock)" },
    fields: {
        name: "Name",
        description: "Description",
        type: "Type",
        unit: "Unit",
        category: "Category",
        brand: "Brand",
        none: "None",
        taxPercent: "VAT %",
        taxPercentHint: "Leave empty to use the category's rate.",
        trackInventory: "Track stock",
        allowNegative: "Allow selling below zero stock",
    },
    options: {
        title: "Options",
        description: "Up to 3 options, such as Size or Colour. Each combination becomes a variant.",
        name: "Option name",
        values: "Values",
        valuesHint: "Separate values with commas: S, M, L",
        add: "Add option",
        remove: "Remove option",
    },
    variants: {
        title: "Variants",
        newTitle: "New combinations",
        newDescription: "These combinations are new. Give each one a price.",
        single: "Price and code",
        variant: "Variant",
        sku: "SKU",
        skuHint: "Leave empty to generate one.",
        price: "Price",
        cost: "Cost",
        mrp: "MRP",
        barcode: "Barcode",
        barcodes: "Barcodes",
        generateBarcode: "Generate barcode",
        status: "Status",
        noOptions: "Default",
        editPrices: "Edit prices",
        pricesTitle: "Prices of {{name}}",
        pricesSaved: "Prices saved.",
        archive: "Archive",
        unarchive: "Bring back",
        archived: "Variant archived.",
        unarchived: "Variant is active again.",
        selected_one: "{{count}} variant selected",
        selected_other: "{{count}} variants selected",
        bulkEdit: "Change prices",
    },
    labels: {
        print: "Print labels",
        printSelected: "Print labels",
        title: "Print barcode labels",
        description: "Stickers for the chosen products. The PDF opens in a new tab; print it at actual size (100 %).",
        copies: "Copies",
        noBarcode: "No barcode yet",
        generate: "Generate",
        generated: "Barcode generated.",
        layout: "Label size",
        layouts: {
            a4_65: "A4 · 65 labels · 38×21 mm",
            a4_40: "A4 · 40 labels · 52×30 mm",
            a4_24: "A4 · 24 labels · 70×37 mm",
            roll_38x25: "Label printer · 38×25 mm",
            roll_50x25: "Label printer · 50×25 mm",
            roll_50x30: "Label printer · 50×30 mm",
        },
        show: "Show on the label",
        fields: {
            businessName: "Business name",
            productName: "Product name",
            price: "Price",
            mrp: "MRP",
            sku: "SKU",
        },
        branch: "Price at",
        basePrice: "Main price",
        skip: "Stickers already used on the sheet",
        skipHelp: "Printing starts after these, left to right, top to bottom.",
        total_one: "{{formatted}} label",
        total_other: "{{formatted}} labels",
        tooMany: "At most 2,000 labels at a time.",
        submit: "Make PDF",
        ready: "Labels ready.",
        open: "Open PDF",
        missing: "Generate a barcode for the products marked first.",
    },
    bulk: {
        title: "Change prices of {{count}} variants",
        field: "Change",
        fields: { price: "Selling price", cost: "Cost" },
        mode: "How",
        modes: { set: "Set to an amount", increase: "Increase by %", decrease: "Decrease by %" },
        amount: "Amount",
        percent: "Percent",
        done_one: "{{count}} variant changed.",
        done_other: "{{count}} variants changed.",
    },
    images: {
        title: "Images",
        description: "Up to 8 photos. The first one is shown in lists.",
        upload: "Add image",
        uploading: "Uploading…",
        remove: "Remove image",
        failed: "The image could not be uploaded. Use a JPEG, PNG or WebP photo.",
        image: "Product image",
    },
    tags: { title: "Tags", empty: "No tags yet. Add them in Catalog setup." },
    locationPrices: {
        title: "Branch prices",
        description: "A branch can sell at its own price. Empty means the main price.",
        mainPrice: "Main price: {{price}}",
        save: "Set",
        useMain: "Use main price",
        saved: "Branch price set.",
        cleared: "Branch uses the main price again.",
        empty: "No branches yet.",
    },
    history: {
        title: "Price history",
        empty: "No price changes yet.",
        fields: { price: "Price", cost: "Cost", mrp: "MRP" },
        allLocations: "All branches",
        none: "none",
    },
    search: {
        label: "Search products",
        placeholder: "Name, SKU or barcode",
        empty: "No products match “{{q}}”.",
        columns: { product: "Product", variant: "Variant", sku: "SKU", price: "Price" },
        rebuild: "Rebuild search index",
        rebuildHint: "Use this if search results look out of date. It runs in the background.",
        rebuildQueued: "The search index is being rebuilt.",
    },
    imports: {
        title: "Import / export",
        description: "Add or update many products at once from Excel or CSV, or download them all.",
        template: "Download a template",
        templateHint: "Fill in one row per variant. The second sheet explains every column.",
        templateXlsx: "Excel template",
        templateCsv: "CSV template",
        download: "Download",
        upload: "Import a file",
        file: "File (.xlsx or .csv)",
        mode: "If some rows have errors",
        modes: { skip_bad_rows: "Import the good rows, skip the bad ones", all_or_nothing: "Import nothing" },
        match: "Products that already exist",
        matches: {
            create_only: "Only add new ones",
            upsert_by_sku: "Update products by SKU",
            upsert_by_phone: "Update customers by phone",
        },
        kind: "What to import or export",
        kinds: { products: "Products", customers: "Customers" },
        check: "Check file",
        uploading: "Uploading…",
        uploadFailed: "The file could not be uploaded. Use an .xlsx or .csv file up to 10 MB.",
        history: "Recent imports",
        empty: "No imports yet.",
        back: "Import / export",
        columns: { file: "File", status: "Status", rows: "Rows", result: "Result", started: "Started" },
        statuses: {
            validating: "Checking…",
            validated: "Checked — ready to apply",
            committing: "Applying…",
            completed: "Done",
            failed: "Failed",
        },
        failures: {
            unreadable: "The file could not be read.",
            missing_columns: "Some required columns are missing. Start from the template.",
            too_many_rows: "The file has more than 10,000 rows. Split it into smaller files.",
            empty_file: "The file is empty.",
            has_errors: "Nothing was imported because some rows have errors.",
            cancelled: "Cancelled.",
            crashed: "Stopped unexpectedly. Run the file again with “Update them by SKU” to finish.",
        },
        tiles: { rows: "Rows", errors: "Rows with errors", create: "New products", update: "Variants to update" },
        summary: {
            rows: "Rows: {{count}}",
            errors: "Rows with errors: {{count}}",
            create: "New products: {{count}}",
            update: "Variants to update: {{count}}",
            result: "Added {{created}}, updated {{updated}}, skipped {{skipped}}",
        },
        preview: "What will be imported",
        previewColumns: { product: "Product", action: "Action", variants: "Variants", row: "Row" },
        actions: { create: "Add", update: "Update" },
        errors: "Rows with errors",
        errorColumns: { row: "Row", column: "Column", message: "Problem" },
        errorReport: "Download the bad rows (Excel)",
        commit: "Apply import",
        cancel: "Cancel import",
        committed: "Import is being applied.",
        cancelledToast: "Import cancelled.",
        export: "Export products",
        exportHint: "Every product and variant in the import layout: edit it and import it back with “Update them by SKU”.",
        exportXlsx: "Export to Excel",
        exportCsv: "Export to CSV",
        exporting: "Exporting…",
        exported: "Export ready: {{count}} rows.",
    },
    aboveMrp: "The price cannot be higher than the MRP.",
    conflict: "Someone else changed this. The latest values are shown; make your change again.",
    setup: {
        title: "Catalog setup",
        description: "Categories, brands, tags and units used by your products.",
        add: "Add",
        edit: "Edit",
        rename: "Rename",
        saved: "Saved.",
        deleted: "Deleted.",
        deleteTitle: "Delete {{name}}?",
        deleteBody: "This cannot be undone.",
        search: "Search {{what}}",
        noMatch: "Nothing matches “{{q}}”.",
        actions: "Actions for {{name}}",
        categories: {
            title: "Categories",
            description: "Up to 3 levels, e.g. Grocery › Rice › Basmati.",
            empty: "No categories yet.",
            parent: "Parent category",
            topLevel: "Top level",
            taxPercent: "Default VAT %",
            add: "Add category",
            addSub: "Add subcategory",
            addSubTo: "Add a subcategory to {{name}}",
            subs_one: "{{count}} subcategory",
            subs_other: "{{count}} subcategories",
            vat: "VAT {{percent}}",
            level: "Level {{level}}",
            expandAll: "Expand all",
            collapseAll: "Collapse all",
            expand: "Show subcategories of {{name}}",
            collapse: "Hide subcategories of {{name}}",
            emptyHint: "Group your products, e.g. Grocery › Rice › Basmati. Shoppers and reports use them.",
        },
        brands: {
            title: "Brands",
            empty: "No brands yet.",
            add: "Add brand",
            emptyHint: "e.g. ACI, Pran, Aarong.",
        },
        tags: {
            title: "Tags",
            empty: "No tags yet.",
            add: "Add tag",
            emptyHint: "Free labels such as New, Eid offer or Organic.",
        },
        units: {
            title: "Units",
            empty: "No units yet.",
            code: "Code",
            codeHint: "Lower-case letters and digits, e.g. packet.",
            precision: "Decimals",
            precisionHint: "0 for pieces; 3 for kg (stock is kept in grams).",
            system: "Built in",
            add: "Add unit",
            name: "Name",
        },
    },
    books: {
        selling: "What are you selling?",
        physical: "Physical product",
        physicalHint: "Shipped to the buyer; has stock.",
        digital: "Digital product",
        digitalHint: "Nothing to ship and no stock: the buyer gets it online after paying.",
        both: "Both",
        bothHint: "One page, two prices — e.g. Hard copy and eBook. Add an option such as Format and tick the digital row.",
        kind: "Kind of digital product",
        kinds: { ebook: "eBook (PDF, read on your website)" },
        digitalRow: "This is the digital version (eBook)",
        ebookBadge: "eBook",
        bookTitle: "Book details",
        bookDescription: "For books: shown on the product page and used for author pages and filters.",
        authors: "Authors",
        addAuthor: "Add author",
        chooseAuthor: "Choose an author",
        newAuthor: "New author's name",
        createAuthor: "Create",
        remove: "Remove",
        roles: { author: "Author", translator: "Translator", editor: "Editor" },
        isbn: "ISBN",
        pages: "Pages",
        edition: "Edition",
        language: "Language",
        year: "Year published",
        publisherHint: "The publisher is the product's brand.",
    },
};
//# sourceMappingURL=catalog.js.map